/**
 * Phase 1 sound: WebAudio synthesis at runtime so we don't need to ship assets.
 * Phase 3+ swaps this for proper recorded stingers via Howler.js (per VISUAL.md §Sound).
 */

import { useSettings } from '@/state/settings';

let ctx: AudioContext | undefined;

function getCtx(): AudioContext | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!ctx) {
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AC) ctx = new AC();
    } catch {
      return undefined;
    }
  }
  if (ctx?.state === 'suspended') {
    void ctx.resume();
  }
  return ctx;
}

function playTone(freq: number, durationMs: number, type: OscillatorType = 'sine', volume = 0.08): void {
  if (useSettings.getState().muted) return;
  const c = getCtx();
  if (!c) return;

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = volume;
  osc.connect(gain).connect(c.destination);
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + durationMs / 1000);
  osc.stop(c.currentTime + durationMs / 1000 + 0.05);
}

function playSequence(notes: { freq: number; durationMs: number; type?: OscillatorType; volume?: number; delayMs?: number }[]): void {
  let acc = 0;
  for (const n of notes) {
    acc += n.delayMs ?? 0;
    setTimeout(() => playTone(n.freq, n.durationMs, n.type, n.volume), acc);
  }
}

export const sounds = {
  fill:         () => playTone(880, 80, 'square', 0.07),
  cancel:       () => playTone(440, 60, 'sine', 0.05),
  reject:       () => playTone(180, 180, 'sawtooth', 0.08),
  notifyInfo:   () => playTone(660, 100, 'sine', 0.04),
  notifyAlert:  () => playSequence([
    { freq: 880, durationMs: 80 },
    { freq: 1200, durationMs: 80, delayMs: 90 },
  ]),
  marginCall:   () => playSequence([
    { freq: 200, durationMs: 200, type: 'sawtooth', volume: 0.1 },
    { freq: 160, durationMs: 200, type: 'sawtooth', volume: 0.1, delayMs: 250 },
    { freq: 200, durationMs: 200, type: 'sawtooth', volume: 0.1, delayMs: 250 },
  ]),
  smsReceived: () => playSequence([
    { freq: 740, durationMs: 60, volume: 0.05 },
    { freq: 988, durationMs: 80, volume: 0.05, delayMs: 70 },
  ]),
  achievementUnlock: () => playSequence([
    { freq: 660, durationMs: 80, volume: 0.06 },
    { freq: 880, durationMs: 80, volume: 0.06, delayMs: 90 },
    { freq: 1320, durationMs: 140, volume: 0.07, delayMs: 90 },
  ]),
  bigWin: () => playSequence([
    { freq: 523, durationMs: 100, volume: 0.07 },
    { freq: 659, durationMs: 100, volume: 0.07, delayMs: 110 },
    { freq: 784, durationMs: 100, volume: 0.07, delayMs: 110 },
    { freq: 1047, durationMs: 200, volume: 0.08, delayMs: 110 },
  ]),
  bigLoss: () => playSequence([
    { freq: 392, durationMs: 100, volume: 0.07, type: 'sawtooth' },
    { freq: 311, durationMs: 100, volume: 0.07, type: 'sawtooth', delayMs: 110 },
    { freq: 247, durationMs: 240, volume: 0.08, type: 'sawtooth', delayMs: 110 },
  ]),
  boot: () => playSequence([
    { freq: 440, durationMs: 60, volume: 0.05 },
    { freq: 660, durationMs: 60, volume: 0.05, delayMs: 70 },
    { freq: 880, durationMs: 100, volume: 0.06, delayMs: 70 },
  ]),
  ascension: () => playSequence([
    { freq: 392, durationMs: 100, volume: 0.07 },
    { freq: 523, durationMs: 100, volume: 0.07, delayMs: 110 },
    { freq: 659, durationMs: 100, volume: 0.07, delayMs: 110 },
    { freq: 784, durationMs: 100, volume: 0.07, delayMs: 110 },
    { freq: 1047, durationMs: 300, volume: 0.09, delayMs: 110 },
  ]),
};
